/**
 * Een optie waarvan een waarde `metadata.deliveryOverride: true` draagt (eerste
 * gebruik: `levering=snel` bij dekbedovertrekken) is een levertijdkeuze. Op de
 * productpagina staat die niet als losse tegelgroep, maar als extra kolom in de
 * prijsmatrix, naast de Print.com-levertijden (urgency). Zo kiest de klant
 * aantal en levertijd in een klik, net als bij elk ander Print.com-product.
 *
 * Een overridekolom zet nooit iets anders dan `standard` in `urgency`: de regel
 * gaat via een andere leverancier (option-supplier-route.ts), en Print.com of de
 * winkelwagen mogen geen onbekende urgency zien. De kolom-id is alleen een
 * sleutel in de matrix (`levering:snel`), nooit een configuratiewaarde.
 *
 * Client-veilig, geen React.
 */

type OptionValueLike = {
  id?: string | number | null;
  value?: string | number | null;
  label?: string | null;
  description?: string | null;
  hidden?: boolean | null;
  metadata?: Record<string, unknown> | null;
};

type OptionLike = {
  id: string;
  label?: string | null;
  default?: unknown;
  description?: string | null;
  values?: OptionValueLike[] | null;
  metadata?: Record<string, unknown> | null;
};

export type DeliveryOverrideColumn = {
  /** Matrixsleutel, bijvoorbeeld `levering:snel`. */
  id: string;
  optionId: string;
  value: string;
  /** Kolomkop zonder de "(ca. ...)"-toevoeging; de datumregel zegt dat al. */
  label: string;
  description: string;
  days: number;
  /** Optionele vrije tekst uit de metadata, bijvoorbeeld "4-6 werkdagen". */
  range: string;
};

export type DeliveryOverrideModel = {
  optionId: string;
  /** De waarde die bij de gewone Print.com-kolommen hoort (bijv. `standaard`). */
  baseValue: string;
  /** Kolomkop voor de standaard Print.com-kolom, uit het label van `baseValue`. */
  baseLabel: string;
  columns: DeliveryOverrideColumn[];
};

const FALLBACK_DAYS = 5;

function isOverrideValue(value: OptionValueLike | null | undefined) {
  return Boolean(value && value.metadata && value.metadata.deliveryOverride === true);
}

/** Zichtbare waarden met een waarde-id; verborgen waarden doen niet mee. */
function usableValues(option: OptionLike) {
  return (option.values || []).filter((value) =>
    value && value.hidden !== true && String(value.value ?? value.id ?? '') !== ''
  );
}

function valueKey(value: OptionValueLike) {
  return String(value.value ?? value.id ?? '');
}

/**
 * Haalt een afsluitende toelichting tussen haakjes weg:
 * "Snel (ca. 5 werkdagen)" wordt "Snel". Zonder haakjes blijft het label gelijk.
 */
export function stripDeliveryLabelSuffix(label: string) {
  const stripped = String(label || '').replace(/\s*\([^()]*\)\s*$/, '').trim();
  return stripped || String(label || '').trim();
}

/** True voor een optie die als matrixkolommen getoond wordt in plaats van als tegels. */
export function isDeliveryOverrideOption(option: OptionLike | null | undefined) {
  if (!option) return false;
  if (option.metadata && option.metadata.renderAsDeliveryColumns === false) return false;
  const values = usableValues(option);
  // Er moet naast de overridewaarde ook een gewone waarde zijn; anders is er
  // geen Print.com-kolom om naast te zetten.
  return values.some(isOverrideValue) && values.some((value) => !isOverrideValue(value));
}

export function deliveryOverrideColumnId(optionId: string, value: string) {
  return `${optionId}:${value}`;
}

/** Het model voor de eerste optie met overridewaarden, of null (product ongewijzigd). */
export function buildDeliveryOverrideModel(options: OptionLike[] | null | undefined): DeliveryOverrideModel | null {
  const option = (options || []).find((candidate) => isDeliveryOverrideOption(candidate));
  if (!option) return null;
  const values = usableValues(option);
  const regular = values.filter((value) => !isOverrideValue(value));
  const defaultValue = option.default !== undefined && option.default !== null ? String(option.default) : '';
  const base = regular.find((value) => valueKey(value) === defaultValue) || regular[0];
  const columns = values.filter(isOverrideValue).map((value): DeliveryOverrideColumn => {
    const metadata = (value.metadata || {}) as Record<string, unknown>;
    const days = Number(metadata.deliveryDays);
    const key = valueKey(value);
    return {
      id: deliveryOverrideColumnId(option.id, key),
      optionId: option.id,
      value: key,
      label: stripDeliveryLabelSuffix(String(value.label || key)),
      description: String(value.description || ''),
      days: Number.isFinite(days) && days > 0 ? days : FALLBACK_DAYS,
      range: typeof metadata.businessDaysRange === 'string' ? metadata.businessDaysRange : '',
    };
  });
  return {
    optionId: option.id,
    baseValue: valueKey(base),
    baseLabel: stripDeliveryLabelSuffix(String(base.label || valueKey(base))),
    columns,
  };
}

export function findDeliveryOverrideColumn(model: DeliveryOverrideModel | null, columnId: string) {
  return model?.columns.find((column) => column.id === columnId) || null;
}

/** De matrixkolom die bij de huidige configuratie hoort. */
export function selectedMatrixColumnId(
  configuration: Record<string, unknown>,
  model: DeliveryOverrideModel | null,
) {
  if (model) {
    const selected = String(configuration[model.optionId] ?? '');
    const column = model.columns.find((candidate) => candidate.value === selected);
    if (column) return column.id;
  }
  return String(configuration.urgency || 'standard');
}

/**
 * De configuratiewijziging voor een klik op (of een prijsaanvraag voor) een
 * kolom. Een overridekolom: urgency `standard` plus de overridewaarde. Een
 * Print.com-kolom: die urgency plus de gewone waarde, zodat een eerder gekozen
 * "snel" niet blijft hangen.
 */
export function configurationPatchForColumn(
  model: DeliveryOverrideModel | null,
  columnId: string,
): Record<string, string> {
  const override = findDeliveryOverrideColumn(model, columnId);
  if (override) return { urgency: 'standard', [override.optionId]: override.value };
  if (!model) return { urgency: columnId };
  return { urgency: columnId, [model.optionId]: model.baseValue };
}

const approxDays: Record<string, (days: number) => string> = {
  nl: (days) => `ca. ${days} werkdagen`,
  en: (days) => `about ${days} working days`,
  de: (days) => `ca. ${days} Werktage`,
  fr: (days) => `environ ${days} jours ouvrés`,
};

/** "ca. 5 werkdagen" in de taal van de pagina, of de vrije tekst uit de metadata. */
export function deliveryOverrideApproxText(column: Pick<DeliveryOverrideColumn, 'days' | 'range'>, locale: string) {
  if (column.range) return column.range;
  const language = String(locale || 'nl').split('-')[0];
  return (approxDays[language] || approxDays.en)(column.days);
}
